/* ── GE custom pickup availability: keep preview + drawer in sync with the variant ──
   Companion to snippets/custom-pickup-preview.liquid and
   sections/custom-pickup-availability.liquid.

   WHY THIS EXISTS
   The drawer sits outside <product-info> (the only node Concept re-renders on a
   variant change), so it can never update itself server-side. The preview line
   was also observed not to re-render. Both are synced here.

   TWO DIFFERENT MECHANISMS, ON PURPOSE:

   1. PREVIEW - template swap. .pickup-availability-preview is a two-column grid
      that Concept gives exactly two children (one info div + the button).
      Rendering both states into it, even with one hidden, changed the spacing
      between the message and the preparation-time line. So only the current
      state is in the live DOM and the other waits in a <template>, which
      renders nothing. We swap the live div for the template's content.

   2. DRAWER - visibility toggle. The list item is a single-column grid, where a
      display:none child collapses cleanly and costs no gap, so both states can
      live in the DOM and we just flip which is shown.

   Either way the markup is server-rendered, so all merchant wording (section
   settings and snippet params) survives untouched - nothing is rebuilt in JS.

   Availability comes from a variant-id → boolean map emitted by the preview
   snippet, using `variant.available` as the proxy for collectability. Valid
   while Garden Express has ONE pickup location (Monbulk): in stock ==
   collectable. A second pickup location means replacing the map with a
   per-location one built from store_availabilities.

   Same pub/sub pattern as overrides #22 / #24 in custom.js. No-ops safely if
   theme.pubsub is ever absent, so it cannot break the page.

   Update-safe: custom asset, no shipped file edited. Loaded by
   snippets/custom-pickup-preview.liquid. */
(function () {
  function swapPreview(isAvailable) {
    var root = document.querySelector('[data-ge-pickup-preview]');
    if (!root) return;

    var wanted = isAvailable ? 'available' : 'unavailable';
    var live = root.querySelector('.pickup-availability-info');
    if (!live) return;
    if (live.getAttribute('data-ge-pickup-state') === wanted) return; // already right

    var tpl = root.querySelector('[data-ge-pickup-tpl="' + wanted + '"]');
    if (!tpl || !tpl.content) return;

    var replacement = tpl.content.firstElementChild;
    if (!replacement) return;

    live.replaceWith(replacement.cloneNode(true));

    // Optional Concept-style behaviour: hide the whole block when unavailable.
    if (root.hasAttribute('data-ge-pickup-hide-unavailable')) {
      root.style.display = isAvailable ? '' : 'none';
    }
  }

  function toggleDrawer(isAvailable) {
    var drawer = document.querySelector('[data-ge-pickup-drawer]');
    if (!drawer) return;
    drawer.querySelectorAll('[data-ge-pickup-state]').forEach(function (el) {
      var wants = el.getAttribute('data-ge-pickup-state') === 'available';
      el.style.display = wants === isAvailable ? '' : 'none';
    });
  }

  function readMap() {
    var script = document.querySelector('[data-ge-pickup-map]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      return null;
    }
  }

  function updateDrawerVariantLine(variant) {
    var line = document.querySelector('[data-ge-pickup-drawer-variant]');
    if (!line || !variant || !variant.options) return;

    // Reuse the option names already in the DOM so the markup and separator
    // match the server-rendered version exactly.
    var names = [];
    line.querySelectorAll('span.flex').forEach(function (span) {
      names.push(span.childNodes[0] ? span.childNodes[0].textContent.trim() : '');
    });

    var parts = variant.options.map(function (value, i) {
      var label = names[i] || '';
      return '<span class="flex gap-2">' + label + '<span class="font-medium">' + value + '</span></span>';
    });

    line.innerHTML = parts.join(',&nbsp;');
  }

  function init() {
    if (!window.theme || !theme.pubsub || !theme.pubsub.subscribe || !theme.pubsub.PUB_SUB_EVENTS) {
      return;
    }

    theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, function (event) {
      var variant = event && event.data && event.data.variant;
      if (!variant) return;

      var map = readMap();
      if (!map || !(variant.id in map)) return; // different product, or no pickup at all

      var isAvailable = map[variant.id] === true;

      swapPreview(isAvailable);
      toggleDrawer(isAvailable);
      updateDrawerVariantLine(variant);
    });
  }

  // Deferred assets run before theme.js, so theme.pubsub may not exist yet.
  if (window.theme && window.theme.pubsub) init();
  else document.addEventListener('DOMContentLoaded', init);
})();
